import { TablePage, Dialog } from 'web-mojo';
import { VendingProduct, VendingProductList } from '@portal/models/VendingProduct.js';
import './ProductView.js';

export default class VendingProductsPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Products & Inventory',
            icon: 'bi-box-seam',
            pageName: 'vending_products',
            route: 'group/vending/products',
            permissions: ['view_payments', 'manage_payments', 'payments', 'manage_group', 'manage_groups'],
            requiresGroup: true,

            Collection: VendingProductList,

            defaultQuery: {
                sort: 'name',
                group: () => app.activeGroup?.id,
            },

            columns: [
                {
                    label: 'Product',
                    key: 'name',
                    formatter: (val, row) => {
                        const thumb = row?.image?.thumbnail;
                        const img = thumb
                            ? `<img src="${thumb}" class="rounded border me-2" style="width: 28px; height: 28px; object-fit: cover; vertical-align: middle;">`
                            : `<i class="bi bi-box-seam text-muted me-2"></i>`;
                        return `${img}${val || '—'}`;
                    },
                },
                {
                    label: 'SKU',
                    key: 'sku',
                    formatter: "default('—')",
                    visibility: 'md',
                },
                {
                    label: 'Price',
                    key: 'price',
                    formatter: 'currency',
                },
                {
                    label: 'Machine',
                    key: 'machine.name',
                    formatter: "default('All Machines')",
                    visibility: 'md',
                },
                {
                    label: 'Stock',
                    key: 'quantity',
                    formatter: (val, row) => {
                        if (!row?.track_inventory) return '<span class="text-muted small">Not tracked</span>';
                        if (val === 0) return '<span class="badge bg-danger">Out of Stock</span>';
                        return `<span class="badge bg-success">${val}</span>`;
                    },
                    visibility: 'md',
                },
                {
                    label: 'Status',
                    key: 'is_active',
                    formatter: "yesnoicon('bi bi-circle-fill text-success', 'bi bi-circle text-secondary')",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Active',   value: 'true' },
                            { label: 'Inactive', value: 'false' },
                        ]
                    },
                },
            ],

            viewDialogOptions: {
                header: false,
                size: 'md',
                footer: false,
            },

            selectable: true,

            batchActions: [
                { label: 'Deactivate', icon: 'bi bi-x-circle', action: 'deactivate' },
                { label: 'Export', icon: 'bi bi-download', action: 'export' },
            ],
            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,
            showRefresh: true,
            addButtonLabel: 'Add Product',
            showExport: false,

            tableOptions: {
                striped: true,
                bordered: false,
                hover: true,
                responsive: true,
            },

            contextMenu: [
                { label: 'Edit',              icon: 'bi bi-pencil', action: 'edit' },
                { label: 'Update Image',      icon: 'bi bi-image', action: 'update-image' },
                { label: 'Update Stock',      icon: 'bi bi-box-seam', action: 'update-stock' },
                { label: 'Deactivate',        icon: 'bi bi-x-circle', action: 'deactivate-product', className: 'text-warning' },
            ],

            tableViewOptions: {
                addRequiresActiveGroup: true,
                hideActivePillNames: ['group'],
            },

            ...options,
        });
    }

    async onActionBatchDeactivate() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Deactivate ${selected.length} product${selected.length > 1 ? 's' : ''}`,
            message: 'Deactivate selected products? They will be removed from all machine menus.',
            confirmText: 'Deactivate',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({ is_active: false })));
        app.toast.success(`Deactivated ${selected.length} product${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchExport() {
        this.tableView.collection.export();
    }

    async onActionUpdateImage(event, el) {
        const id = el.getAttribute('data-id');
        const product = this.collection.get(id);
        if (!product) return;
        const resp = await Dialog.updateModelImage({
            model: product,
            field: 'image',
            title: 'Product Image',
            upload: true,
        }, {
            name: 'image',
            size: 'lg',
            placeholder: 'Upload product image',
        });
        if (resp && resp.status === 200) {
            this.collection.fetch();
        }
    }

    async onActionUpdateStock(event, el) {
        const id = el.getAttribute('data-id');
        const product = this.collection.get(id);
        if (!product) return;

        const result = await app.modal.form({
            title: `Update Stock — ${product.nameOrId()}`,
            size: 'sm',
            fields: [
                { name: 'quantity', type: 'number', label: 'New Stock Quantity', required: true,
                  value: product.get('quantity') ?? 0, min: 0,
                  help: 'Enter the current physical count (absolute value, not a delta).' },
            ],
        });
        if (!result?.submitted) return;

        const response = await product.save({ quantity: result.data.quantity });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to update stock');
            return;
        }
        app.toast.success('Stock updated');
        this.collection.fetch();
    }

    async onActionDeactivateProduct(event, el) {
        const id = el.getAttribute('data-id');
        const product = this.collection.get(id);
        if (!product) return;

        const isActive = product.get('is_active');
        const action = isActive ? 'Deactivate' : 'Reactivate';

        const confirmed = await Dialog.confirm({
            title: `${action} Product`,
            message: isActive
                ? `Deactivate "${product.nameOrId()}"? It will be removed from all machine menus.`
                : `Reactivate "${product.nameOrId()}"?`,
            confirmText: action,
            confirmClass: isActive ? 'btn-warning' : 'btn-success',
        });
        if (!confirmed) return;

        const response = await product.save({ is_active: !isActive });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || `Failed to ${action.toLowerCase()} product`);
            return;
        }
        app.toast.success(`Product ${isActive ? 'deactivated' : 'reactivated'}`);
        this.collection.fetch();
    }
}
